import React, { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Link2, Wallet, BadgeIndianRupee, Copy, Mail, MailCheck, AlertTriangle, MessageCircle } from "lucide-react";
import { Link } from "react-router-dom";

/** Normalise an Indian/international phone number for `wa.me` (digits only, default +91). */
function waNormalize(raw) {
  if (!raw) return "";
  const digits = String(raw).replace(/\D/g, "");
  if (!digits) return "";
  // If 10 digits, prepend 91 (India default). If 11 starting with 0, drop the 0.
  if (digits.length === 10) return "91" + digits;
  if (digits.length === 11 && digits.startsWith("0")) return "91" + digits.slice(1);
  return digits;
}

function waShareUrl(phone, hospitalName, planName, amountInr, publicUrl) {
  const num = waNormalize(phone);
  const msg = encodeURIComponent(
    `Hi ${hospitalName || "Team"},\n\nHere's your secure CareO payment link for *${planName}* (₹${Number(amountInr || 0).toLocaleString("en-IN")}).\n\nPay securely: ${publicUrl}\n\n— CareO Billing`
  );
  return num ? `https://wa.me/${num}?text=${msg}` : `https://wa.me/?text=${msg}`;
}

import { toast } from "sonner";

function StatusBadge({ status, days }) {
  const styles = {
    active: "border-emerald-300 bg-emerald-50 text-emerald-700",
    expiring: "border-amber-300 bg-amber-50 text-amber-700",
    expired: "border-rose-300 bg-rose-50 text-rose-700",
    no_plan: "border-zinc-300 bg-zinc-100 text-zinc-600",
  };
  return (
    <Badge variant="outline" className={styles[status] || styles.no_plan}>
      {status === "active" && `Active · ${days}d left`}
      {status === "expiring" && `Expiring · ${days}d left`}
      {status === "expired" && `Expired ${Math.abs(days || 0)}d ago`}
      {status === "no_plan" && "No plan"}
    </Badge>
  );
}

export default function SuperAdminSubscriptions() {
  const [items, setItems] = useState([]);
  const [plans, setPlans] = useState([]);
  const [links, setLinks] = useState([]);
  const [smtpReady, setSmtpReady] = useState(true);
  const [payOpen, setPayOpen] = useState(false);
  const [linkOpen, setLinkOpen] = useState(false);
  const [target, setTarget] = useState(null);
  const [form, setForm] = useState({ plan_id: "", amount_inr: 0, reference: "", notes: "", gateway: "razorpay", expiry_days: 7, coupon_code: "" });
  const [lastLink, setLastLink] = useState(null);  // populated after generate so we can offer WhatsApp share
  // Coupon preview for the "Mark paid" dialog — validated against the target
  // hospital + plan so the amount auto-recomputes.
  const [couponPreview, setCouponPreview] = useState(null);
  const [couponBusy, setCouponBusy] = useState(false);

  const load = async () => {
    try {
      const [subs, pl, ll, smtp] = await Promise.all([
        api.get("/payments/admin/subscriptions"),
        api.get("/superadmin/plans"),
        api.get("/payments/admin/payment-links"),
        api.get("/settings/saas-smtp"),
      ]);
      setItems(subs.data.items);
      setPlans(pl.data.items);
      setLinks(ll.data.items || []);
      // Real SMTP ready = enabled AND has password configured AND has username
      setSmtpReady(!!(smtp.data?.enabled && smtp.data?.password_set && smtp.data?.username));
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail)); }
  };
  useEffect(() => { load(); }, []);

  const openMarkPaid = (row) => {
    setTarget(row);
    setForm({ plan_id: row.plan_id || (plans[0]?.id || ""), amount_inr: row.plan_price || 0, reference: "", notes: "", gateway: "razorpay", expiry_days: 7, coupon_code: "" });
    setCouponPreview(null);
    setPayOpen(true);
  };
  const openLink = (row) => {
    setTarget(row);
    setForm(s => ({
      ...s, plan_id: row.plan_id || (plans[0]?.id || ""),
      gateway: "razorpay", expiry_days: 7, send_email: true,
      email_override: "", wa_phone: row.contact_phone || "",
    }));
    setLastLink(null);
    setLinkOpen(true);
  };

  const onPlanChange = (id) => {
    const p = plans.find(x => x.id === id);
    setForm(s => ({ ...s, plan_id: id, amount_inr: p?.price_inr || s.amount_inr, coupon_code: "" }));
    setCouponPreview(null);
  };

  const applyCouponInMarkPaid = async () => {
    const code = (form.coupon_code || "").trim().toUpperCase();
    if (!code || !target?.hospital_id) return;
    setCouponBusy(true);
    try {
      const { data } = await api.post("/saas/coupons/validate", {
        code, plan_id: form.plan_id, hospital_id: target.hospital_id,
      });
      setCouponPreview(data);
      setForm(s => ({ ...s, amount_inr: data.final_amount }));
      toast.success(`${code} applied — saves ₹${Number(data.discount_amount).toLocaleString("en-IN")}`);
    } catch (e) {
      setCouponPreview(null);
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Coupon not accepted");
    } finally { setCouponBusy(false); }
  };

  const clearCouponInMarkPaid = () => {
    setCouponPreview(null);
    const p = plans.find(x => x.id === form.plan_id);
    setForm(s => ({ ...s, coupon_code: "", amount_inr: p?.price_inr || s.amount_inr }));
  };

  const submitPaid = async () => {
    try {
      const body = {
        hospital_id: target.hospital_id, plan_id: form.plan_id,
        amount_inr: Number(form.amount_inr), reference: form.reference, notes: form.notes,
      };
      if (couponPreview?.code) body.coupon_code = couponPreview.code;
      await api.post("/payments/admin/mark-paid", body);
      toast.success(couponPreview ? `Subscription extended with ${couponPreview.code} — saved ₹${Number(couponPreview.discount_amount).toLocaleString("en-IN")}` : "Subscription extended");
      setPayOpen(false); load();
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail)); }
  };

  const submitLink = async () => {
    try {
      const { data } = await api.post("/payments/admin/payment-link", {
        hospital_id: target.hospital_id, plan_id: form.plan_id,
        gateway: form.gateway, expiry_days: Number(form.expiry_days),
        send_email: form.send_email !== false,
        email_override: (form.email_override || "").trim() || null,
      });
      const recipients = data.email?.recipients || [];
      const results = data.email?.results || [];
      const trulySent = results.filter(r => r.status === "sent").length;
      const stubbed = results.filter(r => r.status === "sent_stub").length;
      if (trulySent > 0) {
        toast.success(`Payment link created & emailed to ${trulySent} recipient${trulySent === 1 ? "" : "s"}`);
      } else if (stubbed > 0) {
        toast.warning(`Link created — but emails were NOT delivered (SaaS Gmail SMTP not enabled). Configure it at Super Admin → SaaS Email.`, { duration: 7000 });
      } else if (recipients.length === 0 && form.send_email !== false) {
        toast.warning("Link created, but no email on file for this hospital. Copy link below or set contact_email + email_override.");
      } else {
        toast.success("Payment link created");
      }
      const fullUrl = data.public_url?.startsWith("http") ? data.public_url : `${window.location.origin}${data.public_url}`;
      try { await navigator.clipboard.writeText(fullUrl); toast.message("Link copied to clipboard"); } catch (_e) {}
      // Keep the dialog open with a success state so user can WhatsApp the link
      setLastLink({
        ...data,
        public_url: fullUrl,
        delivered: trulySent,
        stubbed: stubbed,
        hospital_name: target?.hospital_name,
        amount_inr: data.amount_inr,
        plan_name: data.plan_name,
      });
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail)); }
  };

  const resendLink = async (linkId) => {
    try {
      const { data } = await api.post(`/payments/admin/payment-link/${linkId}/resend`);
      const trulySent = (data.results || []).filter(r => r.status === "sent").length;
      const stubbed = (data.results || []).filter(r => r.status === "sent_stub").length;
      if (trulySent > 0) toast.success(`Re-sent to ${trulySent} recipient${trulySent === 1 ? "" : "s"}`);
      else if (stubbed > 0) toast.warning("Stub mode — SaaS Gmail SMTP is not enabled.", { duration: 6000 });
      else toast.success("Resend triggered");
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail)); }
  };

  const copyLink = async (url) => {
    const full = url?.startsWith("http") ? url : `${window.location.origin}${url}`;
    try { await navigator.clipboard.writeText(full); toast.success("Copied"); } catch (_e) { toast.error("Copy failed"); }
  };

  return (
    <div className="space-y-6 animate-fade-in" data-testid="superadmin-subscriptions-page">
      <div>
        <div className="section-label">SaaS Console</div>
        <h1 className="font-heading text-3xl font-semibold">Hospital subscriptions</h1>
        <p className="mt-1 text-sm text-ink-muted">{items.length} hospital{items.length === 1 ? "" : "s"} · Manage plans, extend validity, and generate payment links.</p>
      </div>

      {!smtpReady && (
        <Card className="border-amber-300 bg-amber-50/60 p-4" data-testid="smtp-not-ready-banner">
          <div className="flex items-start gap-3">
            <AlertTriangle className="mt-0.5 text-amber-600 shrink-0" size={20} />
            <div className="flex-1">
              <h3 className="font-semibold text-amber-800">SaaS Email is not configured — payment-link emails will NOT be delivered</h3>
              <p className="text-sm text-amber-900/80 mt-1">
                When you generate a payment link or mark-paid, the system tries to email the hospital — but emails go to <b>stub mode</b> until you set up your Gmail App Password.
                Hospitals will not receive the link in their inbox.
              </p>
              <Link to="/app/superadmin/saas-email" className="mt-3 inline-flex items-center gap-1 rounded-md bg-amber-600 hover:bg-amber-700 px-3 py-1.5 text-xs font-semibold text-white">
                <Mail size={14} />Configure SaaS Email now
              </Link>
            </div>
          </div>
        </Card>
      )}

      <Tabs defaultValue="hospitals">
        <TabsList>
          <TabsTrigger value="hospitals" data-testid="subs-tab-hospitals">All hospitals</TabsTrigger>
          <TabsTrigger value="links" data-testid="subs-tab-links">Payment links ({links.length})</TabsTrigger>
        </TabsList>

        <TabsContent value="hospitals" className="mt-4">
          <Card className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-secondary/60 text-left text-[11px] uppercase tracking-wider text-ink-muted">
                <tr>
                  <th className="px-4 py-3">Hospital</th>
                  <th className="px-4 py-3">Plan</th>
                  <th className="px-4 py-3 text-right">Price</th>
                  <th className="px-4 py-3">Valid until</th>
                  <th className="px-4 py-3 text-center">Status</th>
                  <th className="px-4 py-3 text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {items.map(r => (
                  <tr key={r.hospital_id} data-testid={`sub-row-${r.hospital_id.slice(0,6)}`}>
                    <td className="px-4 py-3">
                      <div className="font-semibold">{r.hospital_name}</div>
                      <div className="text-[11px] font-mono text-ink-muted">{r.code}</div>
                    </td>
                    <td className="px-4 py-3">{r.plan_name || <span className="text-ink-muted italic">—</span>}</td>
                    <td className="px-4 py-3 text-right">{r.plan_price ? `₹ ${Number(r.plan_price).toLocaleString("en-IN")}` : "—"}</td>
                    <td className="px-4 py-3 font-mono text-xs">{r.valid_until ? r.valid_until.slice(0,10) : "—"}</td>
                    <td className="px-4 py-3 text-center"><StatusBadge status={r.status} days={r.days_left} /></td>
                    <td className="px-4 py-3">
                      <div className="flex justify-end gap-2">
                        <Button size="sm" variant="outline" onClick={() => openLink(r)} data-testid={`sub-link-${r.hospital_id.slice(0,6)}`}>
                          <Link2 size={14} className="mr-1" />Payment link
                        </Button>
                        <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700" onClick={() => openMarkPaid(r)} data-testid={`sub-paid-${r.hospital_id.slice(0,6)}`}>
                          <BadgeIndianRupee size={14} className="mr-1" />Mark paid
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
                {items.length === 0 && <tr><td colSpan={6} className="px-4 py-10 text-center text-sm text-ink-muted">No hospitals yet.</td></tr>}
              </tbody>
            </table>
          </Card>
        </TabsContent>

        <TabsContent value="links" className="mt-4">
          <Card className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-secondary/60 text-left text-[11px] uppercase tracking-wider text-ink-muted">
                <tr>
                  <th className="px-4 py-3">Hospital</th>
                  <th className="px-4 py-3">Plan</th>
                  <th className="px-4 py-3 text-right">Amount</th>
                  <th className="px-4 py-3">Gateway</th>
                  <th className="px-4 py-3">Expires</th>
                  <th className="px-4 py-3">Emailed to</th>
                  <th className="px-4 py-3 text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {links.map(l => (
                  <tr key={l.id}>
                    <td className="px-4 py-3 font-medium">{l.hospital_name}</td>
                    <td className="px-4 py-3">{l.plan_name}</td>
                    <td className="px-4 py-3 text-right">₹ {Number(l.amount_inr).toLocaleString("en-IN")}</td>
                    <td className="px-4 py-3 capitalize">{l.gateway}</td>
                    <td className="px-4 py-3 font-mono text-xs">{l.expires_at?.slice(0,10)}</td>
                    <td className="px-4 py-3 text-xs">
                      {(() => {
                        const recs = l.email?.recipients || [];
                        const results = l.email?.results || [];
                        const delivered = results.filter(r => r.status === "sent").length;
                        const stubbed = results.filter(r => r.status === "sent_stub").length;
                        if (delivered > 0) {
                          return (
                            <div className="flex items-center gap-1 text-emerald-700">
                              <MailCheck size={12} />
                              <span>Delivered to {delivered}</span>
                              {l.resend_count > 0 && <span className="text-ink-muted ml-1">(resent {l.resend_count}×)</span>}
                            </div>
                          );
                        }
                        if (stubbed > 0) {
                          return (
                            <div className="flex items-center gap-1 text-amber-700" title="SaaS Email not enabled — payment link was not delivered">
                              <AlertTriangle size={12} />
                              <span>Stub · {recs.length} queued</span>
                            </div>
                          );
                        }
                        return <span className="text-ink-muted italic">Not emailed</span>;
                      })()}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex justify-end gap-1">
                        <Button size="sm" variant="ghost" onClick={() => copyLink(l.public_url)} title="Copy link" data-testid={`link-copy-${l.id.slice(0,6)}`}>
                          <Copy size={12} />
                        </Button>
                        <a
                          href={waShareUrl(l.hospital_phone, l.hospital_name, l.plan_name, l.amount_inr, l.public_url)}
                          target="_blank" rel="noreferrer"
                          className="inline-flex h-8 w-8 items-center justify-center rounded-md hover:bg-emerald-50"
                          title={l.hospital_phone ? `WhatsApp ${l.hospital_phone}` : "Open WhatsApp (no number on file)"}
                          data-testid={`link-wa-${l.id.slice(0,6)}`}
                        >
                          <MessageCircle size={14} className="text-emerald-600" />
                        </a>
                        <Button size="sm" variant="ghost" onClick={() => resendLink(l.id)} title="Resend email" data-testid={`link-resend-${l.id.slice(0,6)}`}>
                          <Mail size={12} className="text-brand" />
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
                {links.length === 0 && <tr><td colSpan={7} className="px-4 py-10 text-center text-sm text-ink-muted">No payment links generated yet.</td></tr>}
              </tbody>
            </table>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Mark paid dialog */}
      <Dialog open={payOpen} onOpenChange={(o) => { setPayOpen(o); if (!o) { setCouponPreview(null); } }}>
        <DialogContent>
          <DialogHeader><DialogTitle className="font-heading">Mark hospital as paid (offline)</DialogTitle></DialogHeader>
          <p className="text-sm text-ink-muted">For NEFT, cheque or cash payments received outside the app. Validity extends automatically.</p>
          <div className="grid gap-3">
            <div>
              <Label>Hospital</Label>
              <Input value={target?.hospital_name || ""} disabled />
            </div>
            <div>
              <Label>Plan</Label>
              <select className="w-full rounded-md border bg-background px-3 py-2 text-sm" value={form.plan_id} onChange={e => onPlanChange(e.target.value)} data-testid="paid-plan-select">
                {plans.map(p => <option key={p.id} value={p.id}>{p.name} · ₹{p.price_inr} · {p.duration || "yearly"}</option>)}
              </select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Amount (₹)</Label><Input type="number" value={form.amount_inr} onChange={e => setForm(s => ({ ...s, amount_inr: Number(e.target.value) }))} data-testid="paid-amount" /></div>
              <div><Label>Reference</Label><Input value={form.reference} onChange={e => setForm(s => ({ ...s, reference: e.target.value }))} placeholder="NEFT / Cheque no." /></div>
            </div>
            <div><Label>Notes</Label><Input value={form.notes} onChange={e => setForm(s => ({ ...s, notes: e.target.value }))} /></div>

            {/* Coupon (optional) — validates against target hospital + plan */}
            <div className="rounded-md border border-dashed border-brand/40 bg-brand/5 p-3" data-testid="paid-coupon-block">
              {!couponPreview ? (
                <div className="flex flex-col gap-2 sm:flex-row sm:items-end">
                  <div className="flex-1">
                    <div className="mb-1 text-xs font-semibold text-brand">Apply coupon (optional)</div>
                    <input
                      type="text"
                      value={form.coupon_code}
                      onChange={(e) => setForm(s => ({ ...s, coupon_code: e.target.value.toUpperCase() }))}
                      onKeyDown={(e) => e.key === "Enter" && applyCouponInMarkPaid()}
                      placeholder="e.g. DIWALI25"
                      className="w-full rounded-md border border-border bg-white px-2.5 py-1.5 text-sm font-mono uppercase tracking-wider"
                      data-testid="paid-coupon-input"
                    />
                  </div>
                  <Button
                    type="button"
                    onClick={applyCouponInMarkPaid}
                    disabled={!form.coupon_code.trim() || couponBusy}
                    className="bg-brand hover:bg-brand-700"
                    data-testid="paid-coupon-apply-btn"
                  >
                    {couponBusy ? "Checking…" : "Apply"}
                  </Button>
                </div>
              ) : (
                <div data-testid="paid-coupon-applied">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2 text-sm">
                      <span className="rounded-full bg-emerald-100 px-2 py-0.5 text-[10px] font-semibold text-emerald-700">Applied</span>
                      <span className="font-mono font-semibold text-brand">{couponPreview.code}</span>
                      <span className="text-xs text-ink-muted">
                        ({couponPreview.discount_type === "percent" ? `${couponPreview.discount_value}% off` : `₹${couponPreview.discount_value} off`})
                      </span>
                    </div>
                    <button type="button" onClick={clearCouponInMarkPaid} className="text-ink-muted hover:text-rose-700" title="Remove coupon" data-testid="paid-coupon-remove-btn">
                      ✕
                    </button>
                  </div>
                  <div className="mt-2 space-y-0.5 border-t border-brand/20 pt-1.5 text-xs">
                    <div className="flex justify-between text-ink-muted"><span>Base</span><span>₹ {Number(couponPreview.base_amount).toLocaleString("en-IN")}</span></div>
                    <div className="flex justify-between text-emerald-700"><span>Discount</span><span>−₹ {Number(couponPreview.discount_amount).toLocaleString("en-IN")}</span></div>
                    <div className="flex justify-between font-semibold"><span>Final</span><span>₹ {Number(couponPreview.final_amount).toLocaleString("en-IN")}</span></div>
                  </div>
                </div>
              )}
            </div>
          </div>
          <Button onClick={submitPaid} className="mt-3 bg-emerald-600 hover:bg-emerald-700" data-testid="paid-submit-btn">Record payment & extend</Button>
        </DialogContent>
      </Dialog>

      {/* Payment link dialog */}
      <Dialog open={linkOpen} onOpenChange={(o) => { setLinkOpen(o); if (!o) setLastLink(null); }}>
        <DialogContent>
          <DialogHeader><DialogTitle className="font-heading">{lastLink ? "Payment link created — share it" : "Generate shareable payment link"}</DialogTitle></DialogHeader>

          {!lastLink && (
            <>
              <p className="text-sm text-ink-muted">Creates a one-time URL the hospital can open to pay via Razorpay/PayU. When enabled, the link is auto-emailed to the hospital's admin(s).</p>
              <div className="grid gap-3">
                <div><Label>Hospital</Label><Input value={target?.hospital_name || ""} disabled /></div>
                <div>
                  <Label>Plan</Label>
                  <select className="w-full rounded-md border bg-background px-3 py-2 text-sm" value={form.plan_id} onChange={e => onPlanChange(e.target.value)} data-testid="link-plan-select">
                    {plans.map(p => <option key={p.id} value={p.id}>{p.name} · ₹{p.price_inr}</option>)}
                  </select>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label>Gateway</Label>
                    <select className="w-full rounded-md border bg-background px-3 py-2 text-sm" value={form.gateway} onChange={e => setForm(s => ({ ...s, gateway: e.target.value }))} data-testid="link-gateway-select">
                      <option value="razorpay">Razorpay</option>
                      <option value="payu">PayU India</option>
                    </select>
                  </div>
                  <div><Label>Expiry (days)</Label><Input type="number" value={form.expiry_days} onChange={e => setForm(s => ({ ...s, expiry_days: Number(e.target.value) }))} /></div>
                </div>

                <div className="rounded-lg border bg-sky-50/40 p-3">
                  <label className="flex cursor-pointer items-center gap-2 text-sm font-medium">
                    <input type="checkbox" checked={form.send_email !== false} onChange={e => setForm(s => ({ ...s, send_email: e.target.checked }))}
                      data-testid="link-send-email-toggle" className="h-4 w-4 accent-brand" />
                    <Mail size={14} className="text-brand" />Auto-email this link to the hospital
                  </label>
                  <p className="ml-6 mt-1 text-[11px] text-ink-muted">Sends to contact_email + every active hospital_admin via your configured SaaS Gmail SMTP.</p>
                  {form.send_email !== false && (
                    <div className="ml-6 mt-2">
                      <Label className="text-xs">Also send to (optional)</Label>
                      <Input type="email" value={form.email_override || ""} onChange={e => setForm(s => ({ ...s, email_override: e.target.value }))} placeholder="e.g. ceo@hospital.in" data-testid="link-email-override" />
                    </div>
                  )}
                </div>

                <div className="rounded-lg border bg-emerald-50/40 p-3">
                  <label className="flex items-center gap-2 text-sm font-medium">
                    <MessageCircle size={14} className="text-emerald-600" />WhatsApp number (optional)
                  </label>
                  <p className="ml-6 mt-1 text-[11px] text-ink-muted">After we create the link, you'll see a WhatsApp share button that opens the conversation pre-filled with payment details.</p>
                  <div className="ml-6 mt-2">
                    <Input value={form.wa_phone || ""} onChange={e => setForm(s => ({ ...s, wa_phone: e.target.value }))}
                      placeholder="+91 98765 43210" data-testid="link-wa-phone" />
                  </div>
                </div>
              </div>
              <Button onClick={submitLink} className="mt-3 bg-brand hover:bg-brand-700" data-testid="link-submit-btn">
                <Wallet size={14} className="mr-2" />Generate link
              </Button>
            </>
          )}

          {lastLink && (
            <div className="space-y-4">
              <Card className="border-emerald-300 bg-emerald-50/60 p-4">
                <div className="flex items-start gap-3">
                  <MailCheck className="mt-0.5 shrink-0 text-emerald-600" size={20} />
                  <div className="text-sm">
                    <p className="font-semibold text-emerald-800">Payment link is ready & copied to clipboard</p>
                    {lastLink.delivered > 0
                      ? <p className="mt-1 text-emerald-700">Email delivered to {lastLink.delivered} recipient{lastLink.delivered === 1 ? "" : "s"}.</p>
                      : lastLink.stubbed > 0
                        ? <p className="mt-1 text-amber-700">Email could not be delivered (SaaS Gmail SMTP off). Use WhatsApp or copy the link below.</p>
                        : <p className="mt-1 text-ink-muted">No email recipient — use WhatsApp or copy the link below.</p>}
                  </div>
                </div>
              </Card>

              <div>
                <Label className="text-xs uppercase tracking-wider text-ink-muted">Payment link</Label>
                <div className="mt-1 flex gap-2">
                  <Input value={lastLink.public_url} readOnly className="font-mono text-xs" data-testid="last-link-url" />
                  <Button variant="outline" onClick={async () => { await navigator.clipboard.writeText(lastLink.public_url); toast.success("Copied"); }}><Copy size={14} /></Button>
                </div>
              </div>

              <div className="grid gap-2 md:grid-cols-2">
                <a
                  href={waShareUrl(form.wa_phone, lastLink.hospital_name, lastLink.plan_name, lastLink.amount_inr, lastLink.public_url)}
                  target="_blank" rel="noreferrer"
                  className="flex items-center justify-center gap-2 rounded-md bg-emerald-600 hover:bg-emerald-700 px-4 py-2.5 text-sm font-semibold text-white"
                  data-testid="last-link-wa-share"
                >
                  <MessageCircle size={16} />Open WhatsApp to share
                </a>
                <a
                  href={`mailto:?subject=${encodeURIComponent(`CareO — Payment link for ${lastLink.plan_name}`)}&body=${encodeURIComponent(`Hi ${lastLink.hospital_name},\n\nPay securely: ${lastLink.public_url}\n\n— CareO Billing`)}`}
                  className="flex items-center justify-center gap-2 rounded-md border border-brand bg-white text-brand hover:bg-brand/5 px-4 py-2.5 text-sm font-semibold"
                  data-testid="last-link-mailto"
                >
                  <Mail size={16} />Open in Mail app
                </a>
              </div>

              <div className="flex justify-end gap-2 pt-2">
                <Button variant="outline" onClick={() => { setLastLink(null); }}>Create another</Button>
                <Button onClick={() => { setLastLink(null); setLinkOpen(false); }} className="bg-brand hover:bg-brand-700">Done</Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
